"use client";

import * as React from "react";

type Props = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
};

// Catches client-side render errors below it in the tree, logs them with the
// React component stack (which `app/error.tsx` does NOT receive from Next.js),
// and renders nothing in place of the failing subtree so the rest of the UI
// keeps working — no "Application error" overlay.
//
// Class component is required: `componentDidCatch` is the only API that
// receives `info.componentStack`.
export class ClientErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const digest = (error as Error & { digest?: string }).digest;

    // componentStack is the most useful field here — it lists React component
    // names from innermost to root. Names are mangled in production builds;
    // for readable names either upload source maps to an error tracker or
    // expand the stack in DevTools Console where browser source maps resolve
    // them.
    console.error("[ClientErrorBoundary] Render error", {
      name: error.name,
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
      digest,
      url: typeof window !== "undefined" ? window.location.href : undefined,
      timestamp: new Date().toISOString(),
    });
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}
